/**
 * Cash-flow pricing for fixed-income instruments.
 *
 * Port of crawler/ticker_data_crawler/services/fixed_income_cash_flow.py
 * (year_fraction, _discount_factor, price_from_cash_flows) plus the annual
 * effective IRR solver used by the simulator. The Python module remains the
 * source of truth for materialized risk metrics; this port keeps simulated
 * prices on the same formulas (float precision instead of Decimal, since the
 * simulator is interactive and its results are explicitly labeled estimates).
 */

import { businessDaysBetween, calendarDaysBetween } from './business-calendar';

export type DayCountConvention = 'ACT_365' | 'BUSINESS_252';

export type FixedIncomeFlowType = 'PRINCIPAL' | 'COUPON' | 'AMORTIZATION';

export type FixedIncomeFlow = {
  paymentDate: Date;
  flowType: FixedIncomeFlowType;
  amount: number;
  indexer: string | null;
};

export type FixedIncomeSchedule = {
  referenceDate: Date;
  flows: FixedIncomeFlow[];
  dayCountConvention: DayCountConvention;
};

const BUSINESS_252_DENOMINATOR = 252;

export const yearFraction = (start: Date, end: Date, convention: DayCountConvention = 'ACT_365'): number => {
  if (end.getTime() < start.getTime()) throw new Error('end date must not be before start date');
  if (convention === 'BUSINESS_252') return businessDaysBetween(start, end) / BUSINESS_252_DENOMINATOR;
  return calendarDaysBetween(start, end) / 365;
};

export const discountFactor = (annualYield: number, timeYears: number): number => {
  if (!(1 + annualYield > 0)) throw new Error('annual yield must be greater than -100%');
  return Math.exp(-timeYears * Math.log(1 + annualYield));
};

export const scheduleWithReference = (schedule: FixedIncomeSchedule, referenceDate: Date): FixedIncomeSchedule => ({
  ...schedule,
  referenceDate,
  flows: schedule.flows.filter((flow) => flow.paymentDate.getTime() > referenceDate.getTime()),
});

export const priceFromCashFlows = (schedule: FixedIncomeSchedule, annualYield: number): number => {
  let price = 0;
  for (const flow of schedule.flows) {
    if (flow.paymentDate.getTime() <= schedule.referenceDate.getTime()) continue;
    price +=
      flow.amount *
      discountFactor(annualYield, yearFraction(schedule.referenceDate, flow.paymentDate, schedule.dayCountConvention));
  }
  return price;
};

export const bulletSchedule = (args: {
  referenceDate: Date;
  maturityDate: Date;
  principal: number;
  dayCountConvention?: DayCountConvention;
}): FixedIncomeSchedule => ({
  referenceDate: args.referenceDate,
  flows: [{ paymentDate: args.maturityDate, flowType: 'PRINCIPAL', amount: args.principal, indexer: null }],
  dayCountConvention: args.dayCountConvention ?? 'BUSINESS_252',
});

export type DatedAmount = { date: Date; amount: number };

/**
 * Annual effective IRR of a dated cash-flow vector via bisection. Returns
 * null when no sign change is found (e.g. all flows in the same direction).
 */
export const irrAnnualEffective = (flows: DatedAmount[]): number | null => {
  if (flows.length < 2) return null;
  const start = flows[0].date;
  if (flows.some((flow) => flow.date.getTime() < start.getTime())) {
    throw new Error('cash flows must be ordered chronologically');
  }
  const npv = (rate: number) =>
    flows.reduce((acc, flow) => acc + flow.amount / Math.pow(1 + rate, yearFraction(start, flow.date)), 0);
  let low = -0.9;
  let high = 10;
  let npvLow = npv(low);
  const npvHigh = npv(high);
  if (npvLow * npvHigh > 0) return null;
  for (let iteration = 0; iteration < 200; iteration += 1) {
    const mid = (low + high) / 2;
    const npvMid = npv(mid);
    if (Math.abs(npvMid) < 1e-9) return mid;
    if (npvLow * npvMid < 0) {
      high = mid;
    } else {
      low = mid;
      npvLow = npvMid;
    }
  }
  return (low + high) / 2;
};
